import React, { useState, useEffect } from 'react';
import { Users, Clock, CheckCircle, AlertCircle, XCircle, Plus } from 'lucide-react';
import { RestaurantOrder } from '../types';
import { apiService } from '../services/api';

interface TableViewProps {
  onNewOrder: () => void;
  onStatusFilter?: (status: string) => void;
  statusFilter?: string;
}

interface TableStatus {
  tableNumber: string;
  status: 'available' | 'occupied' | 'reserved';
  currentOrder?: RestaurantOrder;
  customerName?: string;
  orderTime?: string;
  totalAmount?: number;
}

const TableView: React.FC<TableViewProps> = ({ onNewOrder, onStatusFilter, statusFilter = 'all' }) => {
  const [tables, setTables] = useState<TableStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Generate 20 tables with random status distribution
  const generateTables = () => {
    const statuses: ('available' | 'occupied' | 'reserved')[] = [
      'available', 'available', 'available', 'available', 'available', 'available', 'available', 'available', 'available', 'available',
      'occupied', 'occupied', 'occupied', 'occupied', 'occupied', 'occupied',
      'reserved', 'reserved', 'reserved', 'reserved'
    ];
    
    // Shuffle the statuses array to randomize distribution
    const shuffledStatuses = [...statuses].sort(() => Math.random() - 0.5);
    
    return Array.from({ length: 20 }, (_, i) => ({
      tableNumber: String(i + 1).padStart(2, '0'),
      status: shuffledStatuses[i],
    }));
  };

  useEffect(() => {
    loadTableData();
  }, []);

  const loadTableData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get all orders to determine table status
      const orders = await apiService.getRestaurantOrders();
      
      // Initialize all tables as available
      const tableStatuses: TableStatus[] = generateTables();
      
      // Update table statuses based on current orders
      orders.forEach((order) => {
        if (order.tableNumber) {
          const tableIndex = parseInt(order.tableNumber) - 1;
          if (tableIndex >= 0 && tableIndex < tableStatuses.length) {
            tableStatuses[tableIndex] = {
              tableNumber: order.tableNumber,
              status: order.status === 'cancelled' ? 'available' : 'occupied',
              currentOrder: order,
              customerName: order.customerName,
              orderTime: order.orderTime,
              totalAmount: order.totalAmount,
            };
          }
        }
      });
      
      setTables(tableStatuses);
    } catch (err) {
      setError('Failed to load table data');
      console.error('Error loading table data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter tables based on status
  const filteredTables = tables.filter(table => {
    if (statusFilter === 'all') return true;
    return table.status === statusFilter;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'occupied':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'reserved':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'available':
        return <CheckCircle className="w-4 h-4" />;
      case 'occupied':
        return <Users className="w-4 h-4" />;
      case 'reserved':
        return <Clock className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="glass-card p-8 rounded-2xl shadow-card text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading tables...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error State */}
      {error && (
        <div className="glass-card p-6 rounded-2xl shadow-card border-l-4 border-red-500">
          <div className="flex items-center gap-3">
            <XCircle className="w-6 h-6 text-red-500" />
            <div>
              <h3 className="font-semibold text-red-800">Error</h3>
              <p className="text-red-600">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Tables Grid */}
      <div className="glass-card rounded-2xl shadow-card p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredTables.map((table) => (
            <div
              key={table.tableNumber}
              className={`transition-all duration-200 hover:scale-105 ${
                table.status === 'available' 
                  ? 'cursor-pointer' 
                  : 'cursor-default'
              }`}
              onClick={() => {
                if (table.status === 'available') {
                  onNewOrder();
                }
              }}
            >
              {/* SVG Table Representation */}
              <div className="relative w-32 h-28 mx-auto">
                <svg width="128" height="112" viewBox="0 0 128 112" className="transition-all duration-200 hover:scale-105">
                  {/* Table Top */}
                  <ellipse 
                    cx="64" 
                    cy="56" 
                    rx="42" 
                    ry="28" 
                    fill={table.status === 'available' ? '#22c55e' : table.status === 'occupied' ? '#ef4444' : '#eab308'}
                    stroke={table.status === 'available' ? '#16a34a' : table.status === 'occupied' ? '#dc2626' : '#ca8a04'}
                    strokeWidth="2"
                    filter="drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))"
                  />
                  
                  {/* Table Surface Highlight */}
                  <ellipse 
                    cx="64" 
                    cy="48" 
                    rx="38" 
                    ry="22" 
                    fill="rgba(255, 255, 255, 0.3)"
                  />
                  
                  {/* Table Number */}
                  <text 
                    x="64" 
                    y="62" 
                    textAnchor="middle" 
                    className="text-sm font-bold fill-gray-800"
                    fontSize="16"
                    fontWeight="bold"
                  >
                    T{table.tableNumber}
                  </text>
                  
                  {/* Chairs - Top */}
                  <g transform="translate(42, 12)">
                    <rect x="0" y="0" width="10" height="16" rx="2" fill={table.status === 'available' ? '#22c55e' : table.status === 'occupied' ? '#ef4444' : '#eab308'} stroke={table.status === 'available' ? '#16a34a' : table.status === 'occupied' ? '#dc2626' : '#ca8a04'} strokeWidth="1" />
                    <rect x="1" y="1" width="8" height="3" rx="1" fill="#374151" />
                  </g>
                  
                  <g transform="translate(76, 12)">
                    <rect x="0" y="0" width="10" height="16" rx="2" fill={table.status === 'available' ? '#22c55e' : table.status === 'occupied' ? '#ef4444' : '#eab308'} stroke={table.status === 'available' ? '#16a34a' : table.status === 'occupied' ? '#dc2626' : '#ca8a04'} strokeWidth="1" />
                    <rect x="1" y="1" width="8" height="3" rx="1" fill="#374151" />
                  </g>
                  
                  {/* Chairs - Bottom */}
                  <g transform="translate(42, 84)">
                    <rect x="0" y="0" width="10" height="16" rx="2" fill={table.status === 'available' ? '#22c55e' : table.status === 'occupied' ? '#ef4444' : '#eab308'} stroke={table.status === 'available' ? '#16a34a' : table.status === 'occupied' ? '#dc2626' : '#ca8a04'} strokeWidth="1" />
                    <rect x="1" y="1" width="8" height="3" rx="1" fill="#374151" />
                  </g>
                  
                  <g transform="translate(76, 84)">
                    <rect x="0" y="0" width="10" height="16" rx="2" fill={table.status === 'available' ? '#22c55e' : table.status === 'occupied' ? '#ef4444' : '#eab308'} stroke={table.status === 'available' ? '#16a34a' : table.status === 'occupied' ? '#dc2626' : '#ca8a04'} strokeWidth="1" />
                    <rect x="1" y="1" width="8" height="3" rx="1" fill="#374151" />
                  </g>
                </svg>
              </div>

              {/* Table Info */}
              <div className="mt-3 text-center">
                <div className="text-sm font-semibold text-gray-800">Table {table.tableNumber}</div>
                <div className={`text-xs font-medium ${
                  table.status === 'available' 
                    ? 'text-green-600' 
                    : table.status === 'occupied'
                    ? 'text-red-600'
                    : 'text-yellow-600'
                }`}>
                  {table.status.charAt(0).toUpperCase() + table.status.slice(1)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default TableView;
